import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { ChevronDown } from 'lucide-react';
import type { PerformanceRow } from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import { useChartTheme } from '@/components/aperturas/shared';
import { colorNota, fmt } from '@/components/reviews/shared';

/**
 * ¿Los pisos mejor valorados facturan más?
 *
 * Un punto por alojamiento: la nota en el eje X y la métrica elegida en el Y.
 *
 * **La métrica por defecto es el RevPAR y no el bruto, y esto es lo importante
 * de esta pantalla.** El bruto de un piso lo manda su tamaño: un apartamento de
 * seis plazas factura el triple que un estudio tenga la nota que tenga, así que
 * un gráfico de bruto contra nota mide sobre todo cuántas camas hay y la
 * correlación que sale no significa nada. El RevPAR —ingreso por noche
 * disponible, o sea ADR por ocupación— quita de en medio tanto el precio como
 * el tamaño del calendario, y es donde de verdad se ve si una nota alta se paga.
 * El bruto sigue estando en el desplegable porque es lo que se pidió mirar, con
 * su aviso al lado.
 *
 * El color es la zona porque es el otro gran factor de confusión: Zahara en
 * agosto factura más que Barbate con la misma nota, y sin distinguirlas la nube
 * parece decir algo sobre la calidad cuando está hablando de la playa.
 *
 * El tamaño del punto es el número de reseñas: un 5,00 de tres huéspedes y un
 * 4,87 de treinta y cinco no pesan igual, y en un gráfico sin ese matiz el
 * primero tira de la recta tanto como el segundo.
 */

type Metrica = 'revpar' | 'adr' | 'facturacion';

const METRICAS: { clave: Metrica; etiqueta: string; sufijo: string; ayuda: string }[] = [
  {
    clave: 'revpar',
    etiqueta: 'RevPAR',
    sufijo: '€/noche disp.',
    ayuda:
      'Ingreso por noche disponible (ADR × ocupación). Es la comparación limpia: no la infla que el piso sea grande ni que haya estado más meses abierto.',
  },
  {
    clave: 'adr',
    etiqueta: 'ADR',
    sufijo: '€/noche',
    ayuda: 'Precio medio por noche vendida. Dice si una nota alta se cobra más cara, sin mirar cuánto se llena.',
  },
  {
    clave: 'facturacion',
    etiqueta: 'Bruto',
    sufijo: '€',
    ayuda:
      'Facturación del periodo. Ojo: la manda el tamaño del piso más que la nota, así que la correlación que salga aquí hay que leerla con pinzas.',
  },
];

/** Menos de esto, la nota se mueve demasiado para pesar en una recta. */
const MINIMO_RESENAS = 5;

interface Punto {
  nota: number;
  valor: number;
  n: number;
  nombre: string;
  zona: string;
}

/**
 * Coeficiente de Pearson.
 *
 * Devuelve `null` con menos de tres puntos: con dos, la recta pasa por los dos
 * y `r` vale 1 siempre, que es una forma elegante de no decir nada.
 */
function pearson(puntos: Punto[]): number | null {
  const n = puntos.length;
  if (n < 3) return null;
  const mx = puntos.reduce((s, p) => s + p.nota, 0) / n;
  const my = puntos.reduce((s, p) => s + p.valor, 0) / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (const p of puntos) {
    const a = p.nota - mx;
    const b = p.valor - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  if (dx === 0 || dy === 0) return null;
  return num / Math.sqrt(dx * dy);
}

/** Recta de mínimos cuadrados, para dibujarla sobre la nube. */
function recta(puntos: Punto[]): { x1: number; y1: number; x2: number; y2: number } | null {
  const n = puntos.length;
  if (n < 3) return null;
  const mx = puntos.reduce((s, p) => s + p.nota, 0) / n;
  const my = puntos.reduce((s, p) => s + p.valor, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of puntos) {
    num += (p.nota - mx) * (p.valor - my);
    den += (p.nota - mx) ** 2;
  }
  if (den === 0) return null;
  const pendiente = num / den;
  const x1 = Math.min(...puntos.map((p) => p.nota));
  const x2 = Math.max(...puntos.map((p) => p.nota));
  return { x1, y1: my + pendiente * (x1 - mx), x2, y2: my + pendiente * (x2 - mx) };
}

/**
 * `r` en palabras.
 *
 * Los cortes son los de manual (0,3 y 0,5) y el texto dice siempre que esto no
 * demuestra causa: con 49 pisos, la zona y el tamaño metidos por medio, una
 * correlación fuerte puede ser perfectamente que los pisos buenos estén en la
 * playa. Escribirlo evita que el gráfico se use como si probara algo.
 */
function lectura(r: number | null, etiqueta: string): string {
  if (r === null) return 'Hacen falta al menos tres alojamientos con nota para calcular nada.';
  const a = Math.abs(r);
  const sentido = r > 0 ? 'sube' : 'baja';
  if (a < 0.3) return `Casi no hay relación: el ${etiqueta} apenas cambia con la nota.`;
  if (a < 0.5) return `Relación débil: el ${etiqueta} ${sentido} algo con la nota, pero hay mucha dispersión.`;
  return `Relación clara: el ${etiqueta} ${sentido} con la nota. No prueba que sea la causa —la zona y el tamaño del piso también empujan—.`;
}

const CLAVE_ABIERTO = 'performance.correlacion.abierto';

function leeAbierto(): boolean {
  try {
    return localStorage.getItem(CLAVE_ABIERTO) !== '0';
  } catch {
    return true;
  }
}

export default function CorrelacionNota({ rows }: { rows: PerformanceRow[] }) {
  const chart = useChartTheme();
  const [metrica, setMetrica] = useState<Metrica>('revpar');
  const [abierto, setAbierto] = useState(leeAbierto);

  const def = METRICAS.find((m) => m.clave === metrica)!;

  const { puntos, fiables, zonas } = useMemo(() => {
    const puntos: Punto[] = [];
    for (const r of rows) {
      if (r.nota5 === null || r.noches <= 0) continue;
      // RevPAR = ADR × ocupación: el ingreso por noche disponible, sin tener que
      // pedirle al servidor los días del periodo.
      const valor =
        metrica === 'revpar' ? r.adr * (r.ocupacion / 100) : metrica === 'adr' ? r.adr : r.facturacion;
      if (!Number.isFinite(valor) || valor <= 0) continue;
      puntos.push({
        nota: r.nota5,
        valor,
        n: r.nResenas,
        nombre: r.property,
        zona: r.zone || 'otro',
      });
    }
    // La recta y la `r` solo con los que tienen muestra suficiente; la nube los
    // enseña todos, más pequeños, para no esconder pisos de la tabla.
    const fiables = puntos.filter((p) => p.n >= MINIMO_RESENAS);
    const zonas = [...new Set(puntos.map((p) => p.zona))].sort();
    return { puntos, fiables, zonas };
  }, [rows, metrica]);

  const r = pearson(fiables);
  const linea = recta(fiables);
  const colorDeZona = (zona: string) => chart.palette[zonas.indexOf(zona) % chart.palette.length];

  function alternar() {
    setAbierto((v) => {
      try {
        localStorage.setItem(CLAVE_ABIERTO, v ? '0' : '1');
      } catch {
        /* sin persistencia */
      }
      return !v;
    });
  }

  return (
    <div className="card p-0 overflow-hidden">
      <button
        type="button"
        onClick={alternar}
        aria-expanded={abierto}
        className="w-full min-h-12 px-4 py-3 flex items-center gap-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">Nota y facturación</span>
          <span className="block text-[12px] text-sereno-dark/50">
            {r === null
              ? 'Sin datos suficientes'
              : `r = ${fmt(r, 2)} sobre ${fiables.length} alojamientos con ${MINIMO_RESENAS}+ reseñas`}
          </span>
        </span>
        <ChevronDown
          size={18}
          className={classNames('shrink-0 text-sereno-dark/40 transition-transform', abierto && 'rotate-180')}
        />
      </button>

      {abierto && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {METRICAS.map((m) => (
              <button
                key={m.clave}
                type="button"
                onClick={() => setMetrica(m.clave)}
                aria-pressed={metrica === m.clave}
                className={classNames(
                  'min-h-11 px-3 rounded-md text-sm transition-colors',
                  metrica === m.clave
                    ? 'bg-sereno-dark text-white an-active'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
                )}
              >
                {m.etiqueta}
              </button>
            ))}
          </div>

          <p className="text-[12px] text-sereno-dark/50 leading-relaxed">{def.ayuda}</p>

          {puntos.length === 0 ? (
            <p className="py-8 text-center text-sm text-sereno-dark/40">
              Ningún alojamiento con nota y noches vendidas en este periodo.
            </p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <ScatterChart margin={{ top: 8, right: 12, bottom: 24, left: 4 }}>
                  <CartesianGrid stroke={chart.grid} />
                  <XAxis
                    type="number"
                    dataKey="nota"
                    name="Nota"
                    // El dominio no arranca en cero: todas las notas viven entre
                    // el 3,5 y el 5, y con el cero dentro la nube se aplasta en
                    // la esquina derecha y no se distingue un 4,1 de un 4,9.
                    domain={['dataMin - 0.1', 5]}
                    tickFormatter={(v: number) => fmt(v, 1)}
                    tick={{ fontSize: 11, fill: chart.axis }}
                    label={{ value: 'Nota sobre 5', position: 'insideBottom', offset: -14, fontSize: 11, fill: chart.axis }}
                  />
                  <YAxis
                    type="number"
                    dataKey="valor"
                    name={def.etiqueta}
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(Math.round(v)))}
                    tick={{ fontSize: 11, fill: chart.axis }}
                    width={44}
                  />
                  {/* El radio codifica el número de reseñas: cuanta más muestra,
                      más gordo el punto. */}
                  <ZAxis type="number" dataKey="n" range={[35, 260]} name="Reseñas" />
                  <Tooltip
                    cursor={{ stroke: chart.axis, strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as Punto;
                      return (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/10 rounded-lg px-2.5 py-2 text-xs shadow-lg">
                          <p className="font-semibold">{p.nombre}</p>
                          <p className={colorNota(p.nota)}>
                            {fmt(p.nota, 2)} sobre 5 · {p.n} reseñas
                          </p>
                          <p className="text-sereno-dark/60">
                            {def.etiqueta} {fmt(p.valor, 0)} {def.sufijo}
                          </p>
                          <p className="text-sereno-dark/40">{ZONE_LABELS[p.zona] || p.zona}</p>
                        </div>
                      );
                    }}
                  />
                  {linea && (
                    <ReferenceLine
                      segment={[
                        { x: linea.x1, y: linea.y1 },
                        { x: linea.x2, y: linea.y2 },
                      ]}
                      stroke={chart.bar}
                      strokeDasharray="5 4"
                      strokeWidth={1.5}
                      ifOverflow="extendDomain"
                    />
                  )}
                  <Scatter data={puntos} isAnimationActive={false}>
                    {puntos.map((p, i) => (
                      <Cell
                        key={`${p.nombre}-${i}`}
                        fill={colorDeZona(p.zona)}
                        fillOpacity={p.n >= MINIMO_RESENAS ? 0.75 : 0.3}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-sereno-dark/50">
                {zonas.map((z) => (
                  <span key={z} className="inline-flex items-center gap-1">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: colorDeZona(z) }}
                    />
                    {ZONE_LABELS[z] || z}
                  </span>
                ))}
                <span className="text-sereno-dark/35">
                  · Punto más grande, más reseñas. Los translúcidos tienen menos de {MINIMO_RESENAS} y no
                  cuentan para la recta.
                </span>
              </div>

              <p className="text-[12px] text-sereno-dark/60">{lectura(r, def.etiqueta)}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
